import React from 'react';
import Moment from 'react-moment';
import axios from 'axios';
import { connect } from 'react-redux';
import { withStyles} from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import IconButton from '@material-ui/core/IconButton';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const styles = theme => ({
  root: {
    display: 'block',
    width: '100%',
    border: '1px solid #f5f5f5',
    borderRadius: 5,
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    '-ms-overflow-style': '-ms-autohiding-scrollbar',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& thead tr th': {
      padding: 10,
      textAlign: 'center',
      fontSize: 12,
      border: '1px solid #f5f5f5',
      cursor: 'pointer',
    },
    '& tbody tr td': {
      padding: 10,
      border: '1px solid #f5f5f5',
    },
    '& tbody tr:nth-of-type(odd) td': {
      backgroundColor: '#fafafa',
    },
  },
  tableHeadingText: {
    lineHeight: 2,
    '& b': {
      verticalAlign: 'super',
    },
  },
  textCenter: {
    textAlign: 'center',
  },
  textMute: {
    color: '#999999',
  },
});


const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
  };
};
class AcademicReportsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sort: null,
      sortDirection: null,
    };

    this.columns = [
      {
        key: 'details',
        headingWidth: 45,
        content: (entry) => {
          return (
            <React.Fragment>
              <IconButton
                onClick={this.props.onEntryClick.bind(this, entry)}
                className="open-academic-report-details-button"
              >
                <OpenInNewIcon />
              </IconButton>
            </React.Fragment>
          );
        },
        notSortable: true,
      },
      {
        key: 'name',
        headingText: props.t('Name'),
        content: (entry) => {
          return (
            <React.Fragment>
              {entry.name}
            </React.Fragment>
          );
        },
      },
      {
        key: 'start_date',
        headingText: props.t('Start date'),
        content: (entry) => {
          return (
            <React.Fragment>
              <Moment format={'ddd ' + dateFormat}>{entry.start_date}</Moment>
            </React.Fragment>
          );
        },
      },
      {
        key: 'end_date',
        headingText: props.t('End date'),
        content: (entry) => {
          return (
            <React.Fragment>
              <Moment format={'ddd ' + dateFormat}>{entry.end_date}</Moment>
            </React.Fragment>
          );
        },
      },
    ];

    this.onTableHeadingClick = this.onTableHeadingClick.bind(this);
    this.getFinalEntries = this.getFinalEntries.bind(this);
  }

  onTableHeadingClick(column) {
    const {
      sort,
      sortDirection,
    } = this.state;

    if (column.notSortable) {
      return;
    }

    let newSort = column.key;
    let newSortDirection = sortDirection;

    if (!newSortDirection) {
      newSortDirection = 'asc';
    }

    if (newSort === sort) {
      newSortDirection = newSortDirection === 'asc'
        ? 'desc'
        : 'asc';
    }

    this.setState({
      sort: newSort,
      sortDirection: newSortDirection,
    });
  }

  getFinalEntries(entries) {
    const {
      sort,
      sortDirection,
    } = this.state;

    let finalEntries = [ ...entries ];

    finalEntries.sort((a, b) => {
      if (
        sort === 'start_date' ||
        sort === 'end_date'
      ) {
        const aDate = new Date(a[sort] ?? '2000-01-01');
        const bDate = new Date(b[sort] ?? '2000-01-01');

        return aDate - bDate;
      }

      const aSort = a[sort] ?? '';
      const bSort = b[sort] ?? '';

      return aSort.localeCompare(bSort);
    });

    if (sortDirection === 'desc') {
      finalEntries = finalEntries.reverse();
    }

    return finalEntries;
  }

  render() {
    const {
      classes,
      entries,
    } = this.props;
    const {
      sort,
      sortDirection,
    } = this.state;

    const finalEntries = this.getFinalEntries(entries);

    return (
      <div className={`academic-reports-table ${classes.root}`}>
        <table className={classes.table}>
          <thead>
            <tr>
              {this.columns.map((column) => {
                let columnProps = {};
                if (column.headerWidth) {
                  columnProps.width = column.headerWidth;
                }

                return (
                  <th
                    { ...columnProps }
                    key={column.key}
                    onClick={this.onTableHeadingClick.bind(this, column)}
                  >
                    <div className={classes.tableHeadingText}>
                      <b>{column.headingText}</b>
                      {(column.key !== sort || !sortDirection) && !column.notSortable && (
                        <span>
                          <SwapVertIcon className={classes.textMute} fontSize="small" />
                        </span>
                      )}
                      {column.key === sort && sortDirection && !column.notSortable && (
                        <span>
                          {sortDirection === 'asc' && (
                            <ArrowUpwardIcon fontSize="small" />
                          )}
                          {sortDirection === 'desc' && (
                            <ArrowDownwardIcon fontSize="small" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {finalEntries.map((entry) => {
              return (
                <tr key={entry.id}>
                  {this.columns.map((column) => {
                    return (
                      <td key={column.key}>
                        {column.content.bind(this, entry)()}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default connect(mapStateToProps)(
  withStyles(styles)(withTranslation()(withSnackbar(AcademicReportsTable)))
);
